import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Sparkles, Send, Loader2, Check, X } from 'lucide-react';

const FONT_H = { fontFamily: '"Cabinet Grotesk", system-ui, sans-serif' };
const FONT_B = { fontFamily: 'Manrope, system-ui, sans-serif' };

const SUGGESTIONS = [
  "Who hasn't paid yet?",
  "What's on the calendar this week?",
  "Which new leads are waiting on me?",
  "Do I owe any vendors payouts?",
];

const Bullets = ({ text }) => (
  <div className="space-y-1.5">
    {(text || '').split('\n').map(l => l.trim()).filter(Boolean).map((l, i) =>
      /^[-•*]/.test(l) ? (
        <div key={i} className="flex gap-2.5 text-zinc-300 text-[13px] leading-relaxed" style={FONT_B}>
          <span className="w-1 h-1 rounded-full bg-blue-400 mt-[8px] flex-shrink-0" />
          <span>{l.replace(/^[-•*]\s*/, '')}</span>
        </div>
      ) : (
        <p key={i} className="text-zinc-300 text-[13px] leading-relaxed" style={FONT_B}>{l}</p>
      )
    )}
  </div>
);

export const AiBrainView = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [executing, setExecuting] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(localStorage.getItem('ai_brain_session') || (() => {
    const id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem('ai_brain_session', id);
    return id;
  })());

  const loadHistory = useCallback(async () => {
    try {
      const d = await api.get(`/api/admin/ai/history?session_id=${sessionId.current}`);
      setMessages(d.messages.map(m => ({ role: m.role, content: m.content, actions: m.executed ? [] : (m.actions || []), executed: m.executed })));
    } catch (e) { console.error('AI history load failed', e); }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, thinking]);

  const send = async (text) => {
    const message = (text || input).trim();
    if (!message || thinking) return;
    setInput('');
    setMessages(m => [...m.map(x => ({ ...x, actions: [] })), { role: 'user', content: message }]);
    setThinking(true);
    try {
      const res = await api.post('/api/admin/ai/chat', { session_id: sessionId.current, message });
      setMessages(m => [...m, { role: 'assistant', content: res.reply, actions: res.actions }]);
    } catch (e) {
      toast.error(e.message || 'AI failed');
      setMessages(m => [...m, { role: 'assistant', content: '- Something went wrong. Try again.', actions: [] }]);
    }
    setThinking(false);
  };

  const confirm = async (actions, msgIdx) => {
    setExecuting(true);
    try {
      const res = await api.post('/api/admin/ai/execute', { session_id: sessionId.current, actions });
      setMessages(m => m.map((x, i) => i === msgIdx ? { ...x, actions: [] } : x).concat([{ role: 'assistant', content: res.results.join('\n'), actions: [], executed: true }]));
      toast.success('Done');
    } catch (e) { toast.error(e.message || 'Execution failed'); }
    setExecuting(false);
  };

  const dismiss = (msgIdx) => setMessages(m => m.map((x, i) => i === msgIdx ? { ...x, actions: [] } : x));

  return (
    <div className="flex flex-col" style={{ minHeight: 'calc(100vh - 240px)' }} data-testid="ai-brain-view">
      <div className="flex-1 space-y-4 pb-6">
        {messages.length === 0 && !thinking && (
          <div className="pt-6" data-testid="ai-empty-state">
            <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center mb-5">
              <Sparkles className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-white text-[26px] font-black tracking-tighter leading-tight mb-2" style={FONT_H}>Ask OZ anything about<br />your business.</h2>
            <p className="text-zinc-500 text-sm mb-7" style={FONT_B}>OZ answers from live data — or makes changes for you. It always asks before touching anything.</p>
            <div className="space-y-2.5">
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={() => send(s)}
                        className="w-full text-left bg-[#111111] border border-white/5 rounded-2xl px-5 py-4 text-zinc-300 text-sm active:scale-[0.98] transition-transform"
                        style={FONT_B} data-testid={`ai-suggestion-${SUGGESTIONS.indexOf(s)}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, idx) => (
          <div key={idx} data-testid={`ai-msg-${idx}`}>
            {m.role === 'user' ? (
              <div className="flex justify-end">
                <div className="max-w-[85%] bg-white text-black rounded-3xl rounded-br-lg px-5 py-3 text-sm font-semibold" style={FONT_B}>{m.content}</div>
              </div>
            ) : (
              <div className="max-w-[92%]">
                <div className={`rounded-3xl rounded-bl-lg px-5 py-4 ${m.executed ? 'bg-emerald-500/[0.06] border border-emerald-500/15' : 'bg-[#111111] border border-white/5'}`}>
                  <Bullets text={m.content} />
                </div>
                {(m.actions || []).length > 0 && (
                  <div className="mt-2.5 bg-[#111111] border border-blue-500/20 rounded-2xl p-4" data-testid={`ai-actions-${idx}`}>
                    <p className="text-blue-400 text-[10px] uppercase tracking-[0.2em] font-bold mb-3" style={FONT_B}>Confirm before I do this</p>
                    <div className="space-y-2 mb-4">
                      {m.actions.map(a => (
                        <p key={a.action_id} className="text-zinc-300 text-[13px] flex gap-2" style={FONT_B}>
                          <span className="text-blue-400">→</span> {a.label}
                        </p>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => confirm(m.actions, idx)} disabled={executing}
                              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white text-black text-xs font-bold active:scale-95 disabled:opacity-50 transition-transform" style={FONT_B} data-testid={`ai-confirm-${idx}`}>
                        {executing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Confirm
                      </button>
                      <button onClick={() => dismiss(idx)} className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-zinc-400 text-xs font-bold active:scale-95 transition-transform" style={FONT_B} data-testid={`ai-dismiss-${idx}`}>
                        <X className="w-3.5 h-3.5" /> Dismiss
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {thinking && (
          <div className="flex items-center gap-2.5 text-zinc-500 text-sm px-2" style={FONT_B} data-testid="ai-thinking">
            <Loader2 className="w-4 h-4 animate-spin text-blue-400" /> Thinking…
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="sticky bottom-24 z-30">
        <div className="flex gap-2 backdrop-blur-2xl bg-black/70 border border-white/10 rounded-full p-1.5 shadow-[0_10px_40px_rgba(0,0,0,0.6)]">
          <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()}
                 placeholder="Ask OZ anything…" disabled={thinking}
                 className="flex-1 bg-transparent px-4 text-sm text-white placeholder:text-zinc-600 focus:outline-none" style={FONT_B} data-testid="ai-input" />
          <button onClick={() => send()} disabled={thinking || !input.trim()}
                  className="w-11 h-11 flex items-center justify-center rounded-full bg-white text-black active:scale-95 disabled:opacity-30 transition-transform" data-testid="ai-send" aria-label="Send">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
